import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/Icon'
import { fetchPeople, type Person } from '@/features/people/api'
import {
  createAutomationRule,
  createTransition,
  createWorkflowState,
  deleteAutomationRule,
  deleteTransition,
  deleteWorkflowState,
  fetchAutomationRules,
  fetchTransitions,
  fetchWorkflows,
  reorderWorkflowStates,
  updateAutomationRule,
  updateWipLimit,
  type AutomationActionType,
  type AutomationEvent,
  type AutomationRule,
  type WorkflowTransition,
} from '@/features/workflow/api'
import type { ApiError } from '@/lib/api/client'
import type { StateCategory } from '@/types/domain'
import type { Workflow } from '@/types/workflow'

const CATEGORIES: StateCategory[] = ['backlog', 'started', 'blocked', 'done', 'cancelled']
const EVENTS: AutomationEvent[] = [
  'work_item.created',
  'work_item.state_changed',
  'work_item.assigned',
  'work_item.closed',
]
const ACTIONS: AutomationActionType[] = ['set_state', 'set_priority', 'set_assignee']
const PRIORITIES = ['urgent', 'high', 'normal', 'low']
const FIELD = 'border-line bg-surface rounded border px-2 py-1 text-[12px] outline-none focus:border-[color:var(--color-brand-300)]'

/**
 * Configuring the engine that moves every card.
 *
 * The workflow engine — columns, the moves allowed between them, WIP limits and
 * the rules that fire on a change — was fully built and configurable only by a
 * seeder. Everything on this screen already existed in the API; what was
 * missing was any way for the people who own the process to see or change it.
 */
export function WorkflowAuthoring() {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<string | null>(null)

  const workflows = useQuery<Workflow[], ApiError>({
    queryKey: ['workflows'],
    queryFn: fetchWorkflows,
    retry: false,
  })

  const rows = workflows.data ?? []
  const workflow = rows.find((row) => row.id === selected) ?? rows[0]

  return (
    <div className="flex flex-col gap-5">
      <label className="flex max-w-sm flex-col gap-1">
        <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
          {t('workflow.pickWorkflow')}
        </span>
        <select
          value={workflow?.id ?? ''}
          onChange={(event) => setSelected(event.target.value)}
          className="border-line bg-surface rounded border px-2.5 py-1.5 text-[13px]"
        >
          {rows.map((row) => (
            <option key={row.id} value={row.id}>
              {row.name} · {row.appliesTo}
            </option>
          ))}
        </select>
      </label>

      {workflow === undefined ? (
        <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
      ) : (
        <>
          <States workflow={workflow} />
          <Transitions workflow={workflow} />
          <Automations workflow={workflow} />
        </>
      )}
    </div>
  )
}

function States({ workflow }: { workflow: Workflow }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [category, setCategory] = useState<StateCategory>('backlog')

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['workflows'] })
  const states = [...workflow.states].sort((a, b) => a.position - b.position)

  const add = useMutation<Workflow, ApiError, void>({
    mutationFn: () => createWorkflowState(workflow.id, { name: name.trim(), category }),
    onSuccess: () => {
      setName('')
      void refresh()
    },
  })

  const remove = useMutation<Workflow, ApiError, { stateId: string; moveTo: string }>({
    mutationFn: ({ stateId, moveTo }) => deleteWorkflowState(workflow.id, stateId, moveTo),
    onSuccess: () => void refresh(),
  })

  const reorder = useMutation<Workflow, ApiError, string[]>({
    mutationFn: (ids) => reorderWorkflowStates(workflow.id, ids),
    onSuccess: () => void refresh(),
  })

  const limit = useMutation<Workflow, ApiError, { stateId: string; wipLimit: number | null }>({
    mutationFn: ({ stateId, wipLimit }) => updateWipLimit(workflow.id, stateId, wipLimit),
    onSuccess: () => void refresh(),
  })

  const move = (index: number, delta: number) => {
    const ids = states.map((state) => state.id)
    const target = index + delta
    if (target < 0 || target >= ids.length) return
    const next = [...ids]
    const moved = next.splice(index, 1)[0]
    if (moved !== undefined) next.splice(target, 0, moved)
    reorder.mutate(next)
  }

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-ink-900 text-sm font-semibold">{t('workflow.states')}</h3>

      <ul className="border-line bg-surface divide-line divide-y rounded-md border">
        {states.map((state, index) => (
          <li key={state.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
            <span className="text-ink-500 w-6 shrink-0 font-mono text-[11px] tabular-nums">
              {index + 1}
            </span>
            <span className="text-ink-900 min-w-0 flex-1 truncate text-[13px]">{state.name}</span>
            <span className="text-ink-500 font-mono text-[10px] tracking-wide uppercase">
              {t(`state.${state.category}`)}
            </span>

            <label className="flex items-center gap-1">
              <span className="text-ink-500 font-mono text-[10px]">{t('workflow.wipLimit')}</span>
              <input
                inputMode="numeric"
                defaultValue={state.wipLimit === null ? '' : String(state.wipLimit)}
                onBlur={(event) => {
                  const raw = event.target.value.trim()
                  const next = raw === '' ? null : Number(raw)
                  if (next !== state.wipLimit) limit.mutate({ stateId: state.id, wipLimit: next })
                }}
                className={`${FIELD} w-14 text-center font-mono tabular-nums`}
              />
            </label>

            <span className="flex shrink-0 items-center gap-0.5">
              <button type="button" onClick={() => move(index, -1)} title={t('workflow.reorderUp')} aria-label={t('workflow.reorderUp')} className="text-ink-500 hover:text-ink-900 rotate-[-90deg] rounded p-1">
                <Icon name="chevron" className="size-3" />
              </button>
              <button type="button" onClick={() => move(index, 1)} title={t('workflow.reorderDown')} aria-label={t('workflow.reorderDown')} className="text-ink-500 hover:text-ink-900 rotate-90 rounded p-1">
                <Icon name="chevron" className="size-3" />
              </button>
              <button
                type="button"
                onClick={() => {
                  const other = states.find((candidate) => candidate.id !== state.id)
                  if (other === undefined) return
                  if (window.confirm(`${t('workflow.deleteState')} — ${t('workflow.moveCardsTo')} ${other.name}`)) {
                    remove.mutate({ stateId: state.id, moveTo: other.id })
                  }
                }}
                title={t('workflow.deleteState')}
                aria-label={t('workflow.deleteState')}
                className="text-ink-500 hover:text-[color:var(--color-state-blocked)] rounded p-1"
              >
                <Icon name="trash" className="size-3" />
              </button>
            </span>
          </li>
        ))}
      </ul>

      {remove.isError || limit.isError || reorder.isError ? (
        <p className="text-[12px] text-[color:var(--color-state-blocked)]">
          {(remove.error ?? limit.error ?? reorder.error)?.message}
        </p>
      ) : null}

      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (name.trim() !== '') add.mutate()
        }}
      >
        <input value={name} placeholder={t('workflow.stateName')} onChange={(e) => setName(e.target.value)} className={`${FIELD} min-w-40 flex-1`} />
        <select value={category} onChange={(e) => setCategory(e.target.value as StateCategory)} className={`${FIELD} w-32`}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{t(`state.${c}`)}</option>)}
        </select>
        <button type="submit" disabled={add.isPending} className="bg-brand-600 flex items-center gap-1 rounded px-2.5 py-1 text-[12px] text-white disabled:opacity-40">
          <Icon name="plus" className="size-3" />
          {t('workflow.addState')}
        </button>
      </form>

      {add.isError ? (
        <p className="text-[12px] text-[color:var(--color-state-blocked)]">{add.error.message}</p>
      ) : null}
    </section>
  )
}

function Transitions({ workflow }: { workflow: Workflow }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const transitions = useQuery<WorkflowTransition[], ApiError>({
    queryKey: ['transitions', workflow.id],
    queryFn: () => fetchTransitions(workflow.id),
    retry: false,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['transitions', workflow.id] })
  const nameOf = (id: string | null) =>
    id === null ? t('workflow.anyState') : (workflow.states.find((s) => s.id === id)?.name ?? '—')

  const add = useMutation<WorkflowTransition, ApiError, void>({
    mutationFn: () => createTransition(workflow.id, { fromStateId: from === '' ? null : from, toStateId: to }),
    onSuccess: () => void refresh(),
  })

  const remove = useMutation<void, ApiError, string>({
    mutationFn: (id) => deleteTransition(workflow.id, id),
    onSuccess: () => void refresh(),
  })

  const rows = transitions.data ?? []

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-ink-900 text-sm font-semibold">{t('workflow.transitions')}</h3>

      {rows.length === 0 ? (
        <p className="border-line bg-surface text-ink-700 rounded-md border p-3 text-[13px]">
          {t('workflow.noTransitions')}
        </p>
      ) : (
        <ul className="border-line bg-surface divide-line divide-y rounded-md border">
          {rows.map((transition) => (
            <li key={transition.id} className="flex items-center gap-2 px-3 py-2 text-[13px]">
              <span className="text-ink-700">{nameOf(transition.fromStateId)}</span>
              <Icon name="chevron" className="text-ink-500 size-3" />
              <span className="text-ink-900 min-w-0 flex-1">{nameOf(transition.toStateId)}</span>
              <button type="button" onClick={() => remove.mutate(transition.id)} title={t('views.remove')} aria-label={t('views.remove')} className="text-ink-500 hover:text-[color:var(--color-state-blocked)] rounded p-1">
                <Icon name="close" className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (to !== '') add.mutate()
        }}
      >
        <select value={from} onChange={(e) => setFrom(e.target.value)} aria-label={t('workflow.from')} className={`${FIELD} w-40`}>
          <option value="">{t('workflow.anyState')}</option>
          {workflow.states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <Icon name="chevron" className="text-ink-500 size-3" />
        <select value={to} onChange={(e) => setTo(e.target.value)} aria-label={t('workflow.to')} className={`${FIELD} w-40`}>
          <option value="">—</option>
          {workflow.states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button type="submit" disabled={to === '' || add.isPending} className="bg-brand-600 flex items-center gap-1 rounded px-2.5 py-1 text-[12px] text-white disabled:opacity-40">
          <Icon name="plus" className="size-3" />
          {t('workflow.addTransition')}
        </button>
      </form>

      {add.isError ? (
        <p className="text-[12px] text-[color:var(--color-state-blocked)]">{add.error.message}</p>
      ) : null}
    </section>
  )
}

function Automations({ workflow }: { workflow: Workflow }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<{ name: string; event: AutomationEvent; action: AutomationActionType; value: string } | null>(null)

  const rules = useQuery<AutomationRule[], ApiError>({
    queryKey: ['automations', workflow.id],
    queryFn: () => fetchAutomationRules(workflow.id),
    retry: false,
  })

  const people = useQuery<Person[], ApiError>({ queryKey: ['people'], queryFn: fetchPeople, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['automations', workflow.id] })

  const add = useMutation<AutomationRule, ApiError, void>({
    mutationFn: () =>
      createAutomationRule(workflow.id, {
        name: draft?.name.trim() ?? '',
        event: draft?.event ?? 'work_item.created',
        actions: [{ type: draft?.action ?? 'set_priority', value: draft?.value }],
      }),
    onSuccess: () => {
      setDraft(null)
      void refresh()
    },
  })

  const toggle = useMutation<AutomationRule, ApiError, { id: string; isEnabled: boolean }>({
    mutationFn: ({ id, isEnabled }) => updateAutomationRule(workflow.id, id, { isEnabled }),
    onSuccess: () => void refresh(),
  })

  const remove = useMutation<void, ApiError, string>({
    mutationFn: (id) => deleteAutomationRule(workflow.id, id),
    onSuccess: () => void refresh(),
  })

  const valueOptions = (action: AutomationActionType) =>
    action === 'set_state'
      ? workflow.states.map((s) => ({ value: s.id, label: s.name }))
      : action === 'set_priority'
        ? PRIORITIES.map((p) => ({ value: p, label: t(`priority.${p}`) }))
        : (people.data ?? []).map((p) => ({ value: p.id, label: p.name }))

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <h3 className="text-ink-900 text-sm font-semibold">{t('workflow.automations')}</h3>
        <button
          type="button"
          onClick={() => setDraft({ name: '', event: 'work_item.created', action: 'set_priority', value: 'normal' })}
          className="text-brand-600 ms-auto flex items-center gap-1 text-[12px]"
        >
          <Icon name="plus" className="size-3" />
          {t('workflow.addAutomation')}
        </button>
      </div>

      {(rules.data ?? []).length === 0 && draft === null ? (
        <p className="border-line bg-surface text-ink-700 rounded-md border p-3 text-[13px]">
          {t('workflow.noAutomations')}
        </p>
      ) : (
        <ul className="border-line bg-surface divide-line divide-y rounded-md border">
          {(rules.data ?? []).map((rule) => (
            <li key={rule.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-[13px]">
              <span className="text-ink-900 min-w-0 flex-1 truncate font-medium">{rule.name}</span>
              <span className="text-ink-500 text-[12px]">
                {t('workflow.when')} {t(`workflow.event.${rule.event}`)}
              </span>
              <button
                type="button"
                onClick={() => toggle.mutate({ id: rule.id, isEnabled: !rule.isEnabled })}
                className={`rounded px-2 py-0.5 font-mono text-[10px] tracking-wide uppercase ${
                  rule.isEnabled ? 'bg-brand-600 text-white' : 'bg-surface-2 text-ink-500'
                }`}
              >
                {rule.isEnabled ? t('workflow.enabled') : t('workflow.disabled')}
              </button>
              <button type="button" onClick={() => remove.mutate(rule.id)} title={t('workflow.deleteRule')} aria-label={t('workflow.deleteRule')} className="text-ink-500 hover:text-[color:var(--color-state-blocked)] rounded p-1">
                <Icon name="trash" className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {draft === null ? null : (
        <form
          className="border-line bg-surface flex flex-wrap items-center gap-2 rounded-md border p-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (draft.name.trim() !== '') add.mutate()
          }}
        >
          <input value={draft.name} placeholder={t('workflow.ruleName')} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={`${FIELD} min-w-40 flex-1`} />
          <span className="text-ink-500 text-[12px]">{t('workflow.when')}</span>
          <select value={draft.event} onChange={(e) => setDraft({ ...draft, event: e.target.value as AutomationEvent })} className={`${FIELD} w-44`}>
            {EVENTS.map((e) => <option key={e} value={e}>{t(`workflow.event.${e}`)}</option>)}
          </select>
          <span className="text-ink-500 text-[12px]">{t('workflow.then')}</span>
          <select
            value={draft.action}
            onChange={(e) => {
              const action = e.target.value as AutomationActionType
              setDraft({ ...draft, action, value: valueOptions(action)[0]?.value ?? '' })
            }}
            className={`${FIELD} w-36`}
          >
            {ACTIONS.map((a) => <option key={a} value={a}>{t(`workflow.action.${a}`)}</option>)}
          </select>
          <select value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} className={`${FIELD} w-36`}>
            {valueOptions(draft.action).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button type="submit" disabled={add.isPending} className="bg-brand-600 rounded px-2.5 py-1 text-[12px] text-white disabled:opacity-40">
            {t('workflow.save')}
          </button>
          <button type="button" onClick={() => setDraft(null)} className="text-ink-500 px-1.5 py-1 text-[12px]">
            {t('workflow.cancel')}
          </button>
          {add.isError ? (
            <p className="w-full text-[12px] text-[color:var(--color-state-blocked)]">{add.error.message}</p>
          ) : null}
        </form>
      )}

      <p className="text-ink-500 text-[11px] leading-tight">{t('workflow.automationNote')}</p>
    </section>
  )
}
